/**
 * ScheduledMessagesAlertBanner
 * -----------------------------------------------------------------------------
 * Tarja de destaque exibida no topo da área de chat (logo abaixo da tarja de
 * anotações) quando a conversa aberta possui MENSAGENS AGENDADAS pendentes
 * (ou com erro).
 *
 * - Reaproveita a MESMA queryKey (`['scheduled-messages-count', conversationId]`)
 *   e a MESMA RPC (`get_scheduled_messages_count_by_conversation`) já usadas
 *   pelo `ScheduleMessageButton` no header → React Query deduplica e não há
 *   chamada extra ao backend.
 * - Subscreve `postgres_changes` em `public.reminder_queue` filtrado por
 *   `conversation_id` para invalidar o cache em tempo real (canal nomeado
 *   com `useId()` para não colidir com o canal do botão).
 * - Botão (X) dispensa a tarja apenas na sessão atual; se chegar um novo
 *   agendamento (count aumenta), a tarja volta a aparecer.
 * - Clique abre o `ConversationScheduledMessagesModal` já existente (mesmo
 *   modal aberto pelo botão do header), sem alterar nada do fluxo atual.
 *
 * Cores: laranja/âmbar (mesma do botão `AlarmClock` quando há itens).
 * Se houver `failed_count > 0`, troca para `destructive` para sinalizar erro.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-12 v1.0.0 - Criação inicial (espelho de ContactNotesAlertBanner).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, X } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

import { ConversationScheduledMessagesModal } from './ConversationScheduledMessagesModal';

interface ScheduledCount {
  pending_count: number;
  failed_count: number;
  total_count: number;
}

interface ScheduledMessagesAlertBannerProps {
  /** ID da conversa aberta. Sem ele, o componente não renderiza. */
  conversationId?: string;
  /** Contexto da inbox repassado ao modal de listagem (mesmo do botão do header). */
  conversationContext?: {
    inboxId?: string;
    inboxName?: string;
    channelType?: string;
    lastInboundAt?: string | null;
  };
}

export function ScheduledMessagesAlertBanner({
  conversationId,
  conversationContext,
}: ScheduledMessagesAlertBannerProps) {
  const qc = useQueryClient();

  // ID único por instância — evita colisão de canal Realtime com o do botão.
  const instanceId = useId();

  const [openList, setOpenList] = useState(false);
  // Controle de "dispensado" apenas na sessão atual.
  const [dismissed, setDismissed] = useState(false);
  // Guarda o último count visto, para reexibir caso chegue novo agendamento.
  const lastSeenCountRef = useRef<number>(0);

  // Reseta o estado quando troca de conversa.
  useEffect(() => {
    setDismissed(false);
    lastSeenCountRef.current = 0;
  }, [conversationId]);

  // Reaproveita a MESMA queryKey do ScheduleMessageButton (cache compartilhado).
  const { data: counts } = useQuery<ScheduledCount>({
    queryKey: ['scheduled-messages-count', conversationId],
    enabled: !!conversationId,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_scheduled_messages_count_by_conversation' as never,
        { p_conversation_id: conversationId } as never,
      );
      if (error) throw error;
      const list = (data ?? []) as ScheduledCount[] | ScheduledCount | null;
      const row = Array.isArray(list) ? list[0] : list;
      return {
        pending_count: Number(row?.pending_count ?? 0),
        failed_count: Number(row?.failed_count ?? 0),
        total_count: Number(row?.total_count ?? 0),
      };
    },
  });

  // Realtime: invalida a queryKey quando reminder_queue muda nesta conversa.
  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`scheduled-banner-${conversationId}-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminder_queue',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          qc.invalidateQueries({
            queryKey: ['scheduled-messages-count', conversationId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, qc, instanceId]);

  const total = counts?.total_count ?? 0;
  const hasFailed = (counts?.failed_count ?? 0) > 0;

  // Se o count aumentou desde a última visualização, força reexibição.
  useEffect(() => {
    if (total > lastSeenCountRef.current) {
      setDismissed(false);
    }
    lastSeenCountRef.current = total;
  }, [total]);

  // Sem conversa ou sem agendamentos → não renderiza nada.
  if (!conversationId || total <= 0 || dismissed) return null;

  const baseLabel =
    total === 1
      ? 'Esta conversa possui 1 mensagem agendada — clique para visualizar'
      : `Esta conversa possui ${total} mensagens agendadas — clique para visualizar`;
  const label = hasFailed ? `${baseLabel} (contém erro)` : baseLabel;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        onClick={() => setOpenList(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenList(true);
          }
        }}
        className={cn(
          'flex items-center gap-2 px-3 md:px-4 py-2',
          'cursor-pointer transition-colors',
          'animate-in fade-in slide-in-from-top-1',
          hasFailed
            ? 'border-b border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15'
            : 'border-b border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15',
        )}
      >
        <AlarmClock className="h-4 w-4 shrink-0" aria-hidden />
        <span
          className={cn(
            'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold leading-none',
            hasFailed
              ? 'bg-destructive text-destructive-foreground'
              : 'bg-amber-500 text-white',
          )}
        >
          {total > 99 ? '99+' : total}
        </span>
        <span className="flex-1 truncate text-xs md:text-sm font-medium">
          {label}
        </span>
        <button
          type="button"
          aria-label="Dispensar aviso"
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
          }}
          className={cn(
            'shrink-0 rounded p-0.5',
            hasFailed
              ? 'text-destructive/80 hover:bg-destructive/15 hover:text-destructive'
              : 'text-amber-700/80 dark:text-amber-400/80 hover:bg-amber-500/15 hover:text-amber-700 dark:hover:text-amber-300',
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {openList && (
        <ConversationScheduledMessagesModal
          open={openList}
          onOpenChange={setOpenList}
          conversationId={conversationId}
          conversationContext={conversationContext}
        />
      )}
    </>
  );
}
